import React from 'react';
import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import {
  assignKeywordToFolder,
  addFolder,
  assignMultipleKeywordsToFolder,
  toggleFolderExpand // Import new action
} from '../redux/actions';

const ItemTypes = {
  KEYWORD: 'keyword',
};

// Added visibleFolderIds and folderFilterActive props
const FolderItem = ({ folder, allFolders, level = 0, visibleFolderIds, folderFilterActive }) => {
  const dispatch = useDispatch();
  const allKeywordsFromStore = useSelector(state => state.keywords.allKeywords);

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.KEYWORD,
    drop: (droppedItem) => {
      const { allSelectedIds } = droppedItem;
      if (allSelectedIds && allSelectedIds.length > 1) {
        const keywordObjectsToAssign = allSelectedIds
          .map(id => {
            const kw = allKeywordsFromStore.find(k => k.id === id);
            return kw ? { id: kw.id, text: kw.originalText, data: kw.data, isActive: kw.isActive } : null;
          })
          .filter(Boolean);
        if (keywordObjectsToAssign.length > 0) {
          dispatch(assignMultipleKeywordsToFolder(folder.id, keywordObjectsToAssign));
        }
      } else {
        const singleKeywordItem = {
            id: droppedItem.draggedKeywordId,
            text: droppedItem.text,
            data: droppedItem.data,
            isActive: droppedItem.isActive
        };
        dispatch(assignKeywordToFolder(folder.id, singleKeywordItem));
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  }));

  let backgroundColor = '#f9f9f9'; // Default for the droppable area
  if (canDrop && isOver) {
    backgroundColor = '#e6ffe6';
  } else if (canDrop && !isOver) {
    backgroundColor = '#fafcfa';
  }

  const handleAddSubfolder = () => {
    const subfolderName = window.prompt(`Enter name for subfolder in "${folder.name}":`);
    if (subfolderName && subfolderName.trim() !== '') {
      dispatch(addFolder(subfolderName.trim(), folder.id));
    }
  };

  const handleToggleExpand = () => {
    dispatch(toggleFolderExpand(folder.id));
  };

  const childFoldersOriginal = allFolders.filter(f => f.parentId === folder.id);
  // Filter children based on whether their ID is in the visibleFolderIds set
  const childFoldersToDisplay = childFoldersOriginal.filter(child => visibleFolderIds.has(child.id));
  const hasChildren = childFoldersOriginal.length > 0; // Base "hasChildren" on original children for the icon

  const indentStyle = {
    marginLeft: `${level * 20}px`,
    marginBottom: '2px', // Reduced margin slightly
  };

  const folderHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '8px', // Consistent padding
    backgroundColor: backgroundColor, // Apply drop indicator here
    border: '1px solid #ddd', // Border for the header part only
    borderRadius: '4px',
    minHeight: '40px', // Ensure enough height
    transition: 'background-color 0.2s ease',
  };

  const expandCollapseBtnStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    marginRight: '5px',
    padding: '0 5px', // Add some padding
    fontSize: '1em',
    color: '#333',
    minWidth: '20px', // Ensure button has width even if icon is small
  };

  const folderNameStyle = {
    flexGrow: 1,
    cursor: 'default', // Default cursor for name, expand button is pointer
    marginLeft: hasChildren ? '0px' : '25px', // Indent name if no expand icon
  };

  const addSubfolderBtnStyle = {
    fontSize: '0.8em',
    padding: '3px 6px',
    marginLeft: '10px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    backgroundColor: 'white',
    cursor: 'pointer',
  };

  // Conditionally render the component itself
  if (!visibleFolderIds.has(folder.id)) {
    return null;
  }

  return (
    <div style={indentStyle} className="folder-item-container">
      <div ref={drop} style={folderHeaderStyle}> {/* Drop target is the folder header */}
        {hasChildren && ( // Show icon if there are *any* children, even if not currently visible by filter
          <button
            onClick={handleToggleExpand}
            style={expandCollapseBtnStyle}
            aria-expanded={folder.isExpanded}
            title={folder.isExpanded ? "Collapse" : "Expand"}
          >
            {folder.isExpanded ? '▼' : '►'}
          </button>
        )}
        <span style={folderNameStyle} title={folder.name}>
          {folder.name}
        </span>
        <span style={{ fontSize: '0.8em', color: '#666', marginRight: '10px' }}>
          ({folder.keywords.length})
        </span>
        <button
          onClick={handleAddSubfolder}
          style={addSubfolderBtnStyle}
        >
          + Add Subfolder
        </button>
      </div>

      {/* Render children only if expanded AND there are children to display based on filter */}
      {hasChildren && folder.isExpanded && childFoldersToDisplay.length > 0 && (
        <div className="subfolder-list" style={{ paddingTop: '2px', paddingLeft: '0px' }}>
          {childFoldersToDisplay.map(child => (
            <FolderItem
              key={child.id}
              folder={child}
              allFolders={allFolders}
              level={level + 1}
              visibleFolderIds={visibleFolderIds} // Pass prop down
            />
          ))}
        </div>
      )}
      {/* Optional: Show a message if children are filtered out but parent is expanded */}
      {hasChildren && folder.isExpanded && childFoldersToDisplay.length === 0 && folderFilterActive && (
         <div style={{ paddingLeft: '25px', fontSize: '0.8em', color: '#777'}}> (No sub-folders match filter) </div>
      )}
    </div>
  );
};

export default FolderItem;
